import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Globe, Bot, Megaphone, LayoutDashboard, ArrowRight } from 'lucide-react';

const solutions = [
  {
    icon: Globe,
    title: 'Sites Web Intelligents',
    description: 'Sites vitrines, landing pages, espaces de commande ou de devis. Conçus pour convertir, pas juste pour exister.',
    features: ['Optimisé conversion', 'Design sur-mesure', 'Mobile-first'],
    color: 'primary',
  },
  {
    icon: Bot,
    title: 'Assistants IA & Automatisation',
    description: 'Réponses automatiques 24/7, qualification des demandes, génération de devis, relances intelligentes.',
    features: ['Disponible 24/7', 'Multilingue', 'Auto-qualification'],
    color: 'accent',
  },
  {
    icon: Megaphone,
    title: 'Acquisition & Visibilité',
    description: 'Campagnes Meta Ads, génération de leads, tests d\'offres et scaling progressif.',
    features: ['Meta Ads', 'Lead generation', 'ROI mesuré'],
    color: 'primary',
  },
  {
    icon: LayoutDashboard,
    title: 'Organisation & Pilotage',
    description: 'Centralisation des leads, suivi simplifié, priorisation et tableaux de bord clairs.',
    features: ['CRM simplifié', 'Dashboards', 'Priorités claires'],
    color: 'accent',
  },
];

export const SolutionsPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      
      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label mb-4 inline-block">
            Solutions
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Des solutions qui{' '}
            <span className="gradient-text">travaillent pour vous</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Chaque brique peut fonctionner seule ou s'intégrer dans un système global. 
            Tout dépend de vos besoins réels.
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-ui-sound-hover
              className="group relative p-8 rounded-2xl card-gradient card-hover"
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                solution.color === 'primary' 
                  ? 'bg-primary/10 group-hover:bg-primary/20' 
                  : 'bg-accent/10 group-hover:bg-accent/20'
              } transition-colors`}>
                <solution.icon className={`w-7 h-7 ${
                  solution.color === 'primary' ? 'text-primary' : 'text-accent'
                }`} />
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                {solution.title}
              </h3>
              <p className="text-muted-foreground mb-6">
                {solution.description}
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-2">
                {solution.features.map((feature) => (
                  <span
                    key={feature}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Hover Arrow */}
              <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className={`w-5 h-5 ${
                  solution.color === 'primary' ? 'text-primary' : 'text-accent'
                }`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Link to="/solutions">
            <Button variant="outline" size="lg" className="group">
              Voir toutes nos solutions
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
